import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";

import { ContextMenu } from "./ContextMenu.js";
import { JdLogo } from "./JdLogo.js";
import type { SourceFold } from "../decompiler/SourcePrinter.js";
import type { ClassReference, SourceTab } from "../model/types.js";

import styles from "./CodePane.module.css";

interface CodePaneProps {
  readonly tabs: readonly SourceTab[];
  readonly activeTabId: string | null;
  readonly onActivate: (tabId: string) => void;
  readonly onClose: (tabId: string) => void;
  readonly onCloseAll: () => void;
  readonly onCloseOthers: (tabId: string) => void;
  readonly onCopyRelativePath: (relativePath: string) => void;
  readonly onExportDecompiledSource: (reference: ClassReference) => void;
  readonly onChooseFiles: () => void;
}

interface TabContextMenuState {
  readonly tab: SourceTab;
  readonly x: number;
  readonly y: number;
}

interface HighlightedCodeProps {
  readonly source: string;
  readonly html: string | null;
  readonly label: string;
  readonly revealScope: string;
  readonly revealLine: number | null;
  readonly revealRequestId: number;
  readonly folds: readonly SourceFold[];
}

interface RevealLifecycle {
  readonly finish: () => void;
}

interface PlainCodeProps {
  readonly source: string;
  readonly foldByStartLine: ReadonlyMap<number, SourceFold>;
  readonly collapsedStartLines: ReadonlySet<number>;
  readonly hiddenLines: ReadonlySet<number>;
  readonly onToggleFold: (startLine: number) => void;
}

function PlainCode({
  source,
  foldByStartLine,
  collapsedStartLines,
  hiddenLines,
  onToggleFold,
}: PlainCodeProps) {
  const lines = source.split("\n");
  return (
    <pre class={styles.plainCode}>
      {lines.map((line, index) => {
        const lineNumber = index + 1;
        const fold = foldByStartLine.get(lineNumber);
        const collapsed = collapsedStartLines.has(lineNumber);
        const hidden = hiddenLines.has(lineNumber);

        return (
          <span
            key={index}
            class={`${styles.plainLine} ${hidden ? styles.foldedLine : ""}`}
            data-line={lineNumber}
          >
            {fold === undefined ? null : (
              <button
                type="button"
                class={styles.foldToggle}
                aria-label={`${collapsed ? "Expand" : "Collapse"} lines ${fold.startLine} through ${fold.endLine}`}
                aria-expanded={!collapsed}
                title={collapsed ? "Expand section" : "Collapse section"}
                onClick={() => onToggleFold(lineNumber)}
              />
            )}
            {line}
            {collapsed ? (
              <span class={styles.foldPlaceholder}> … {'}'}</span>
            ) : null}
            {index + 1 < lines.length ? "\n" : null}
          </span>
        );
      })}
    </pre>
  );
}

function foldsByStartLine(
  folds: readonly SourceFold[],
): ReadonlyMap<number, SourceFold> {
  const result = new Map<number, SourceFold>();

  for (const fold of folds) {
    if (fold.endLine <= fold.startLine) {
      continue;
    }
    const existing = result.get(fold.startLine);
    if (existing === undefined || fold.endLine > existing.endLine) {
      result.set(fold.startLine, fold);
    }
  }

  return result;
}

function hiddenFoldLines(
  folds: readonly SourceFold[],
  collapsedStartLines: ReadonlySet<number>,
): ReadonlySet<number> {
  const result = new Set<number>();

  for (const fold of folds) {
    if (!collapsedStartLines.has(fold.startLine)) {
      continue;
    }
    for (let line = fold.startLine + 1; line <= fold.endLine; line++) {
      result.add(line);
    }
  }

  return result;
}

function HighlightedCode({
  source,
  html,
  label,
  revealScope,
  revealLine,
  revealRequestId,
  folds,
}: HighlightedCodeProps) {
  const container = useRef<HTMLDivElement>(null);
  const reveals = useRef(new Map<HTMLElement, RevealLifecycle>());
  const animatedRevealRequests = useRef(new Map<string, number>());
  const [collapsedStartLines, setCollapsedStartLines] = useState<
    ReadonlySet<number>
  >(() => new Set<number>());
  const foldByStartLine = useMemo(() => foldsByStartLine(folds), [folds]);
  const hiddenLines = useMemo(
    () => hiddenFoldLines(folds, collapsedStartLines),
    [folds, collapsedStartLines],
  );

  const toggleFold = useCallback((startLine: number): void => {
    setCollapsedStartLines((current) => {
      const next = new Set(current);
      if (next.has(startLine)) {
        next.delete(startLine);
      } else {
        next.add(startLine);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setCollapsedStartLines(new Set<number>());
  }, [source]);

  useEffect(() => {
    if (revealLine === null || revealRequestId === 0) {
      return;
    }

    setCollapsedStartLines((current) => {
      let changed = false;
      const next = new Set(current);
      for (const fold of folds) {
        if (
          fold.startLine < revealLine &&
          revealLine <= fold.endLine &&
          next.delete(fold.startLine)
        ) {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [folds, revealLine, revealRequestId]);

  useEffect(() => {
    return () => {
      for (const lifecycle of [...reveals.current.values()]) {
        lifecycle.finish();
      }
    };
  }, []);

  useEffect(() => {
    const root = container.current;
    if (root === null || html === null || html.length === 0) {
      return;
    }

    for (const line of root.querySelectorAll<HTMLElement>("[data-line]")) {
      for (const control of line.querySelectorAll<HTMLElement>(
        "[data-fold-control]",
      )) {
        control.remove();
      }

      const lineNumber = Number(line.dataset.line);
      const hidden = hiddenLines.has(lineNumber);
      line.classList.toggle(styles.foldedLine!, hidden);

      const fold = foldByStartLine.get(lineNumber);
      if (fold === undefined) {
        continue;
      }

      const collapsed = collapsedStartLines.has(lineNumber);
      const button = document.createElement("button");
      button.type = "button";
      button.className = styles.foldToggle!;
      button.dataset.foldControl = "toggle";
      button.dataset.foldStartLine = String(lineNumber);
      button.setAttribute("aria-expanded", String(!collapsed));
      button.setAttribute(
        "aria-label",
        `${collapsed ? "Expand" : "Collapse"} lines ${fold.startLine} through ${fold.endLine}`,
      );
      button.title = collapsed ? "Expand section" : "Collapse section";
      line.prepend(button);

      if (collapsed) {
        const placeholder = document.createElement("span");
        placeholder.className = styles.foldPlaceholder!;
        placeholder.dataset.foldControl = "placeholder";
        placeholder.textContent = " … }";
        line.append(placeholder);
      }
    }
  }, [collapsedStartLines, foldByStartLine, hiddenLines, html]);

  useEffect(() => {
    if (revealLine === null || revealRequestId === 0) {
      return;
    }
    if (hiddenLines.has(revealLine)) {
      return;
    }
    if (animatedRevealRequests.current.get(revealScope) === revealRequestId) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      if (animatedRevealRequests.current.get(revealScope) === revealRequestId) {
        return;
      }

      const scroller = container.current;
      const line = scroller?.querySelector<HTMLElement>(
        `[data-line="${revealLine}"]`,
      );
      if (scroller === null || scroller === undefined) {
        return;
      }
      if (line === null || line === undefined) {
        scroller.scrollTo({ top: 0 });
        return;
      }

      animatedRevealRequests.current.set(revealScope, revealRequestId);

      const scrollerBounds = scroller.getBoundingClientRect();
      const lineBounds = line.getBoundingClientRect();
      const top =
        scroller.scrollTop +
        lineBounds.top -
        scrollerBounds.top -
        scroller.clientHeight / 2 +
        lineBounds.height / 2;
      scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

      reveals.current.get(line)?.finish();

      const revealClass = styles.revealedLine!;
      let timeout = 0;
      const finish = (): void => {
        line.removeEventListener("animationend", handleAnimationEnd);
        if (timeout !== 0) {
          clearTimeout(timeout);
          timeout = 0;
        }
        line.classList.remove(revealClass);
        if (reveals.current.get(line)?.finish === finish) {
          reveals.current.delete(line);
        }
      };
      const handleAnimationEnd = (event: AnimationEvent): void => {
        if (event.target === line) {
          finish();
        }
      };

      line.classList.remove(revealClass);
      void line.offsetWidth;
      line.addEventListener("animationend", handleAnimationEnd);
      line.classList.add(revealClass);
      timeout = window.setTimeout(finish, 1400);
      reveals.current.set(line, { finish });
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [hiddenLines, html, revealLine, revealRequestId, revealScope]);

  const handleClick = useCallback(
    (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const button = target.closest<HTMLButtonElement>(
        "button[data-fold-start-line]",
      );
      if (button === null || !container.current?.contains(button)) {
        return;
      }
      const startLine = Number(button.dataset.foldStartLine);
      if (Number.isInteger(startLine)) {
        toggleFold(startLine);
      }
    },
    [toggleFold],
  );

  return (
    <div
      ref={container}
      class={styles.code}
      tabIndex={0}
      aria-label={label}
      onClick={handleClick}
    >
      {html === null || html.length === 0 ? (
        <PlainCode
          source={source}
          foldByStartLine={foldByStartLine}
          collapsedStartLines={collapsedStartLines}
          hiddenLines={hiddenLines}
          onToggleFold={toggleFold}
        />
      ) : (
        <div
          class={styles.highlightedCode}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}

function formatHex(bytes: Uint8Array): string {
  const lines = [
    "OFFSET    00 01 02 03 04 05 06 07 08 09 0A 0B 0C 0D 0E 0F  ASCII",
  ];

  for (let offset = 0; offset < bytes.length; offset += 16) {
    const hex: string[] = [];
    let ascii = "";

    for (let index = 0; index < 16; index++) {
      const value = bytes[offset + index];
      if (value === undefined) {
        hex.push("  ");
        ascii += " ";
      } else {
        hex.push(value.toString(16).toUpperCase().padStart(2, "0"));
        ascii += value >= 0x20 && value <= 0x7e
          ? String.fromCharCode(value)
          : ".";
      }
    }

    lines.push(
      `${offset.toString(16).toUpperCase().padStart(8, "0")}  ${hex.join(" ")}  ${ascii}`,
    );
  }

  return lines.join("\n");
}

function HexViewer({ bytes, label }: { readonly bytes: Uint8Array; readonly label: string }) {
  const content = useMemo(() => formatHex(bytes), [bytes]);
  return (
    <pre class={styles.hexViewer} tabIndex={0} aria-label={label}>
      {content}
    </pre>
  );
}

export function CodePane({
  tabs,
  activeTabId,
  onActivate,
  onClose,
  onCloseAll,
  onCloseOthers,
  onCopyRelativePath,
  onExportDecompiledSource,
  onChooseFiles,
}: CodePaneProps) {
  const [contextMenu, setContextMenu] = useState<TabContextMenuState | null>(
    null,
  );
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;
  const highlighting =
    activeTab?.status === "loading" &&
    activeTab.statusText === "Applying syntax highlighting…";

  return (
    <section class={styles.pane} aria-label="File viewer">
      <div class={styles.tabs} role="tablist" aria-label="Open files">
        {tabs.map((tab) => {
          const active = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              class={`${styles.tab} ${active ? styles.activeTab : ""}`}
              role="presentation"
              title={tab.subtitle}
              onContextMenu={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setContextMenu({ tab, x: event.clientX, y: event.clientY });
              }}
            >
              <button
                type="button"
                role="tab"
                aria-selected={active}
                class={styles.tabLabel}
                onClick={() => onActivate(tab.id)}
              >
                {tab.status === "loading" ? (
                  <span class={styles.tabSpinner} aria-hidden="true" />
                ) : null}
                <span class={styles.tabTitle}>{tab.title}</span>
              </button>
              <button
                type="button"
                class={styles.closeTab}
                aria-label={`Close ${tab.title}`}
                onClick={() => onClose(tab.id)}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {activeTab === null ? (
        <div class={styles.empty}>
          <JdLogo className={styles.emptyMark!} />
          <h2>Select a file or class</h2>
          <p>Open an item from the file tree, or load files from disk.</p>
          <button type="button" onClick={onChooseFiles}>
            Open File…
          </button>
        </div>
      ) : (
        <div class={styles.editor}>
          {activeTab.status === "loading" ? (
            <div class={styles.loading}>
              <span class={styles.spinner} aria-hidden="true" />
              <strong>{activeTab.statusText}</strong>
              {highlighting ? (
                <span>Syntax highlighting is running in a background worker.</span>
              ) : activeTab.classReference !== null ? (
                <span>Referenced OpenJDK modules are fetched only when needed.</span>
              ) : null}
            </div>
          ) : activeTab.status === "error" ? (
            <div class={styles.error} role="alert">
              <strong>Unable to open {activeTab.title}</strong>
              {activeTab.error?.trim() ? <code>{activeTab.error}</code> : null}
            </div>
          ) : activeTab.contentKind === "binary" && activeTab.bytes !== null ? (
            <HexViewer
              bytes={activeTab.bytes}
              label={`${activeTab.title} hex viewer`}
            />
          ) : (
            <HighlightedCode
              source={activeTab.text}
              html={activeTab.highlightedHtml}
              label={`${activeTab.title} contents`}
              revealScope={activeTab.id}
              revealLine={activeTab.revealLine}
              revealRequestId={activeTab.revealRequestId}
              folds={activeTab.folds}
            />
          )}
          <footer class={styles.statusBar}>
            <span>
              {activeTab.classReference?.internalName ?? activeTab.subtitle}
            </span>
            <span>{activeTab.statusText}</span>
          </footer>
        </div>
      )}
      {contextMenu === null ? null : (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: "Close",
              onSelect: () => onClose(contextMenu.tab.id),
            },
            {
              label: "Close All",
              onSelect: onCloseAll,
            },
            {
              label: "Close Others",
              disabled: tabs.length <= 1,
              onSelect: () => onCloseOthers(contextMenu.tab.id),
            },
            {
              label: "Copy Relative Path",
              separatorBefore: true,
              onSelect: () =>
                onCopyRelativePath(contextMenu.tab.relativePath),
            },
            {
              label: "Export Decompiled Source",
              disabled: contextMenu.tab.classReference === null,
              onSelect: () => {
                const reference = contextMenu.tab.classReference;
                if (reference !== null) {
                  onExportDecompiledSource(reference);
                }
              },
            },
          ]}
        />
      )}
    </section>
  );
}
